import { Controller } from '@hotwired/stimulus'

const UNALLOWED_TAB_IDS = ['fulfillment_window_overages', 'authless_services', 'suspended_services']

export default class extends Controller {
  static values = {
    reportingCycleStatusUrl: String
  }

  connect() {
    this.handleSidebarClosed = this.handleSidebarClosed.bind(this)
    this.handleSnapshotChanged = this.handleSnapshotChanged.bind(this)

    window.addEventListener('galette:sidebar:close', this.handleSidebarClosed)
    window.addEventListener('reporting-cycle:snapshot-changed', this.handleSnapshotChanged)
  }

  disconnect() {
    window.removeEventListener('galette:sidebar:close', this.handleSidebarClosed)
    window.removeEventListener('reporting-cycle:snapshot-changed', this.handleSnapshotChanged)
  }

  handleSidebarClosed(event) {
    // Snapshots live in the page-owned #app-sidebar; ignore other sidebars (e.g. the time-study drawer)
    if (event?.detail?.sidebar && event.detail.sidebar.id !== 'app-sidebar') return

    // Only handle if we're viewing a snapshot
    const url = new URL(window.location.href)
    if (!url.searchParams.has('snapshot_id')) return

    // Remove snapshot_id from URL
    url.searchParams.delete('snapshot_id')
    history.pushState({}, '', url.toString())

    // Show tabs again
    this.toggleTabs(true)

    // Reload frames without snapshot_id
    this.reloadReportingCycleStatus(url.searchParams)
    this.reloadFrame('summary', url.searchParams)
    this.reloadFrame('clients', url.searchParams)
  }

  handleSnapshotChanged(event) {
    const { snapshotId } = event.detail

    // Build new URL with or without snapshot_id parameter
    const url = new URL(window.location.href)
    if (snapshotId && snapshotId !== '') {
      url.searchParams.set('snapshot_id', snapshotId)
      this.toggleTabs(false)
      this.switchToAllowedTabIfNeeded()
    } else {
      url.searchParams.delete('snapshot_id')
      this.toggleTabs(true)
    }

    // Update the URL without navigation
    history.pushState({}, '', url.toString())

    // Reload specific turbo frames with the new snapshot_id
    this.reloadReportingCycleStatus(url.searchParams)
    this.reloadFrame('summary', url.searchParams)
    this.reloadFrame('clients', url.searchParams)
  }

  switchToAllowedTabIfNeeded() {
    // If we are in one of the unallowed tabs, move to "unit delivered" tab (id: tab-summary)
    const unallowedTabIds = UNALLOWED_TAB_IDS.map(id => `tab-${id}`)
    const tablist = document.querySelector('.tab-list__desktop[role="tablist"]')
    const activeTab = tablist ? tablist.querySelector('button.tab-list__tab.tab-list__tab--active') : null

    if (activeTab && unallowedTabIds.includes(activeTab.id)) {
      const summaryTab = document.getElementById('tab-summary')
      if (summaryTab && !summaryTab.classList.contains('active')) {
        summaryTab.click()
      }
    }
  }

  toggleTabs(show) {
    UNALLOWED_TAB_IDS.forEach(tab_id => {
      const tab = document.getElementById(`tab-${tab_id}`)
      const mobileTab = document.getElementById(`tab-${tab_id}-mobile`)
      if (tab) {
        if (show) {
          tab.classList.remove('d-none')
          mobileTab?.classList.remove('d-none')
        } else {
          tab.classList.add('d-none')
          mobileTab?.classList.add('d-none')
        }
      }
    })
  }

  reloadReportingCycleStatus(searchParams) {
    const frame = document.getElementById('reporting-cycle-status')
    if (!frame) return

    // Use the URL provided from Ruby
    const url = new URL(this.reportingCycleStatusUrlValue, window.location.origin)

    // Copy all existing search params (provider_type, date_range_type, etc.)
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.forEach((value, key) => {
      url.searchParams.set(key, value)
    })

    // Update snapshot_id in the URL
    if (searchParams.has('snapshot_id')) {
      url.searchParams.set('snapshot_id', searchParams.get('snapshot_id'))
    } else {
      url.searchParams.delete('snapshot_id')
    }

    // Reload the frame
    frame.src = url.toString()
  }

  reloadFrame(frameId, searchParams) {
    const frame = document.getElementById(`${frameId}-tablist-frame`)
    if (!frame) return

    const currentUrl = new URL(window.location.href)
    const pathParts = currentUrl.pathname.split('/')

    // Replace the last part of the path with the new controller name
    // e.g., /admin/org/service_records/summary/display -> /admin/org/service_records/summary/clients
    pathParts[pathParts.length - 1] = frameId === 'summary' ? 'display' : frameId

    const pushUrlObj = new URL(pathParts.join('/'), window.location.origin)

    searchParams.forEach((value, key) => {
      pushUrlObj.searchParams.append(key, value)
    })

    frame.src = pushUrlObj.toString()
  }
}
